import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { CreateFullPageStep } from '@carbon/ibm-products';

import TagsInput from 'Components/Common/TagsInput/TagsInput';
import { InputElement } from 'Components/Common/TagsInput/types';
import { CONTACT_ROLE_MAX_LENGTH } from 'utils/validationUtils';
import { LocalCategory } from '../../types';
import styles from '../../InitialSetupModal.module.scss';
import CategoryInfoCard from './CategoryInfoCard';

type Props = {
    title: MessageDescriptor;
    description: MessageDescriptor;
    categories: LocalCategory[];
    isDisabled: boolean;
    onAddCategory: (name: string) => void;
    onRemoveCategory: (categoryId: string) => void;
    renderStep?: boolean;
};

const CategoriesStep = ({
    title,
    description,
    categories,
    isDisabled,
    onAddCategory,
    onRemoveCategory,
    renderStep = true,
}: Props) => {
    const intl = useIntl();
    const elements: InputElement[] = categories.map((category) => ({ id: category.id, name: category.name }));

    const content = (
        <div className={styles.stepContainer}>
            <CategoryInfoCard />
            <div className={styles.categoriesContainer}>
                {!renderStep && <h4 className={styles.sectionTitle}>{intl.formatMessage(title)}</h4>}
                <p>{intl.formatMessage(description)}</p>
                <TagsInput
                    textInputId="initial-configuration-new-category"
                    elements={elements}
                    onAdd={onAddCategory}
                    onRemove={(element) => {
                        if (element?.id) {
                            onRemoveCategory(element.id);
                        }
                    }}
                    isDisabled={false}
                    messages={tagMessages}
                    maxLength={CONTACT_ROLE_MAX_LENGTH}
                />
            </div>
        </div>
    );

    if (!renderStep) {
        return content;
    }

    return (
        <CreateFullPageStep
            title={intl.formatMessage(title)}
            fieldsetLegendText=""
            disableSubmit={isDisabled}
        >
            {content}
        </CreateFullPageStep>
    );
};

const tagMessages = defineMessages({
    title: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.CategoriesStep.title',
        defaultMessage: 'Nowa rola lub funkcja',
    },
    titlePlaceholder: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.CategoriesStep.titlePlaceholder',
        defaultMessage: 'Wprowadź nazwę nowej roli lub funkcji',
    },
    elementAlreadyExist: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.CategoriesStep.elementAlreadyExist',
        defaultMessage: 'Taka rola lub funkcja już istnieje. Dodaj inną.',
    },
    formName: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.CategoriesStep.formName',
        defaultMessage: 'Formularz ról i funkcji kontaktów parafialnych',
    },
    elementTooLong: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.CategoriesStep.elementTooLong',
        defaultMessage: 'Nazwa może mieć maksymalnie {max} znaków.',
    },
    elementAdded: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.CategoriesStep.elementAdded',
        defaultMessage: 'Dodano rolę lub funkcję.',
    },
});

export default CategoriesStep;
